import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { lovable } from "@/integrations/lovable";
import { Modal } from "@/components/ds/Modal";
import { Button } from "@/components/ds/Button";
import { FloatInput } from "@/components/ds/FloatInput";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function LoginModal({ open, onClose }: Props) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const { error } = await signIn(email, password);
    setBusy(false);
    if (error) return toast.error(error);
    toast.success("Bem-vindo");
    onClose();
  }

  async function signInGoogle() {
    setBusy(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.href,
    });
    if (result.error) {
      setBusy(false);
      toast.error("Não foi possível entrar com Google");
      return;
    }
    if (result.redirected) return;
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Entrar"
      description="Acesse todo o conteúdo do blog Logcomex.AI"
    >
      <button
        type="button"
        onClick={signInGoogle}
        disabled={busy}
        className="w-full flex items-center justify-center gap-3 rounded-xl border border-foreground/10 bg-background hover:bg-muted/40 transition-colors px-4 py-3 text-sm font-medium disabled:opacity-60"
      >
        <GoogleIcon />
        Continuar com Google
      </button>

      <div className="flex items-center gap-3 text-xs text-muted-foreground my-1">
        <div className="h-px flex-1 bg-foreground/10" />
        ou
        <div className="h-px flex-1 bg-foreground/10" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <FloatInput
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <FloatInput
          label="Senha"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
        />
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Aguarde…" : "Entrar"}
        </Button>
      </form>

      <p className="text-center text-xs text-muted-foreground pt-1">
        O acesso é liberado por convite do administrador.
      </p>
    </Modal>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4-5.5 4-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.5 12 2.5 6.8 2.5 2.6 6.7 2.6 12s4.2 9.5 9.4 9.5c5.4 0 9-3.8 9-9.2 0-.6-.1-1.1-.2-1.6H12z"/>
      <path fill="#4285F4" d="M21.6 12.3c0-.6-.1-1.1-.2-1.6H12v3.9h5.5c-.2 1-.9 2-1.9 2.7l3 2.3c1.8-1.6 2.9-4 3-7.3z"/>
      <path fill="#FBBC05" d="M5.6 14.3a6.1 6.1 0 0 1 0-4.6L2.4 7.4A9.5 9.5 0 0 0 2.6 16l3-2.3z"/>
      <path fill="#34A853" d="M12 21.5c2.6 0 4.8-.9 6.4-2.4l-3-2.3c-.8.6-2 1-3.4 1-2.6 0-4.8-1.7-5.5-4.1l-3.1 2.4C4.8 19.4 8.1 21.5 12 21.5z"/>
    </svg>
  );
}

export const CONTENT_ACCESS_KEY = "lc.contentAccess";
export type ContentAccess = "public" | "restricted";

export function getContentAccess(): ContentAccess {
  if (typeof window === "undefined") return "public";
  return (localStorage.getItem(CONTENT_ACCESS_KEY) as ContentAccess) || "public";
}
